import * as THREE from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { LoadingManager } from '../utils/LoadingManager';
import { InputController } from '../utils/InputController';
import { CollisionSystem } from '../utils/CollisionSystem';
import { createTextSprite } from '../utils/TextSprite';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';

// Character controller interface
export interface CharacterControllerBase {
  update(delta: number, inputController: InputController, collisionSystem: CollisionSystem, camera: THREE.Camera): void;
  setCamera(camera: THREE.PerspectiveCamera): void;
  setInputController(inputController: InputController): void;
  getIsWalking(): boolean;
  startDancing(): void;
  stopDancing(): void;
  getIsDancing(): boolean;
  getCameraHorizontalAngle(): number;
  getPosition(): THREE.Vector3;
}

// Character controller class
export class CharacterController implements CharacterControllerBase {
  private character: THREE.Group;
  private velocity: THREE.Vector3;
  private direction: THREE.Vector3;
  private rotation: THREE.Euler;
  private moveSpeed: number;
  private turnSpeed: number;
  private jumpForce: number;
  private gravity: number;
  private isJumping: boolean;
  private isGrounded: boolean;
  private height: number;
  private mouseSensitivity: number;
  private isWalking: boolean;
  private walkAnimations: TWEEN.Group;
  private leftArmMesh: THREE.Mesh;
  private rightArmMesh: THREE.Mesh;
  private leftLegMesh: THREE.Mesh;
  private rightLegMesh: THREE.Mesh;
  private camera: THREE.PerspectiveCamera | null;
  private cameraVerticalAngle: number;
  private cameraHorizontalAngle: number;
  private cameraDistance: number;
  private minCameraDistance: number;
  private maxCameraDistance: number;
  private inputController: InputController | null;
  private nameLabel: THREE.Sprite | null = null;
  
  constructor(character: THREE.Group) {
    this.character = character;
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.direction = new THREE.Vector3(0, 0, 0);
    this.rotation = new THREE.Euler(0, 0, 0);
    this.moveSpeed = 5;
    this.turnSpeed = 2.5;
    this.jumpForce = 5;
    this.gravity = 15;
    this.isJumping = false;
    this.isGrounded = true;
    this.height = 1.8;
    this.mouseSensitivity = 0.002;
    this.isWalking = false;
    this.walkAnimations = new TWEEN.Group();
    
    // Camera settings
    this.camera = null;
    this.cameraVerticalAngle = 0;
    this.cameraHorizontalAngle = Math.PI;
    this.cameraDistance = 5;
    this.minCameraDistance = 2;
    this.maxCameraDistance = 10;
    this.inputController = null;
    
    // Get references to limb meshes
    this.leftArmMesh = this.character.getObjectByName('leftArm') as THREE.Mesh;
    this.rightArmMesh = this.character.getObjectByName('rightArm') as THREE.Mesh;
    this.leftLegMesh = this.character.getObjectByName('leftLeg') as THREE.Mesh;
    this.rightLegMesh = this.character.getObjectByName('rightLeg') as THREE.Mesh;
    
    // Setup walk animations
    this.setupWalkAnimations();
    
    // Setup mouse capture
    this.setupMouseCapture();

    // Find name label
    this.character.traverse((child) => {
      if (child instanceof THREE.Sprite) {
        this.nameLabel = child;
      }
    });
  }
  
  private setupWalkAnimations(): void {
    const duration = 500; // Duration for a full swing cycle
    const swingAngle = Math.PI / 4; // 45 degrees swing
    
    // Left Arm animation
    const leftArmTween = new TWEEN.Tween({ rotation: -swingAngle }, this.walkAnimations)
      .to({ rotation: swingAngle }, duration)
      .repeat(Infinity)
      .yoyo(true)
      .onUpdate((obj) => {
        if (this.leftArmMesh) {
          this.leftArmMesh.rotation.x = obj.rotation;
        }
      });
    
    // Right Arm animation (opposite phase)
    const rightArmTween = new TWEEN.Tween({ rotation: swingAngle }, this.walkAnimations)
      .to({ rotation: -swingAngle }, duration)
      .repeat(Infinity)
      .yoyo(true)
      .onUpdate((obj) => {
        if (this.rightArmMesh) {
          this.rightArmMesh.rotation.x = obj.rotation;
        }
      });
    
    // Left Leg animation (opposite to left arm)
    const leftLegTween = new TWEEN.Tween({ rotation: swingAngle }, this.walkAnimations)
      .to({ rotation: -swingAngle }, duration)
      .repeat(Infinity)
      .yoyo(true)
      .onUpdate((obj) => {
        if (this.leftLegMesh) {
          this.leftLegMesh.rotation.x = obj.rotation;
        }
      });
    
    // Right Leg animation (opposite to right arm)
    const rightLegTween = new TWEEN.Tween({ rotation: -swingAngle }, this.walkAnimations)
      .to({ rotation: swingAngle }, duration)
      .repeat(Infinity)
      .yoyo(true)
      .onUpdate((obj) => {
        if (this.rightLegMesh) {
          this.rightLegMesh.rotation.x = obj.rotation;
        }
      });
    
    // Start all animations
    leftArmTween.start();
    rightArmTween.start();
    leftLegTween.start();
    rightLegTween.start();
  }
  
  private setupMouseCapture(): void {
    // Add click event listener to capture mouse only on non-mobile devices
    document.addEventListener('click', () => {
      if (this.inputController && !this.inputController.isMobile && !this.inputController.getPointerLockState()) {
        this.inputController.requestPointerLock();
      }
    });
  }
  
  public update(
    delta: number,
    inputController: InputController,
    collisionSystem: CollisionSystem,
    camera: THREE.PerspectiveCamera
  ): void {
    // If input is disabled, don't process movement
    if (!inputController.enabled) return;
    
    const wasWalking = this.isWalking;
    this.isWalking = false;

    // Calculate movement from input
    this.processMovementInput(delta, inputController);
    
    // Apply gravity and handle collisions
    this.applyGravity(delta, collisionSystem);
    
    // Handle jumping
    this.handleJump(inputController, collisionSystem);
    
    // Update animations based on movement
    if (this.isWalking !== wasWalking) {
      this.updateAnimations();
    }
    
    // Update camera position
    this.updateCameraPosition(camera, inputController, delta);
  }
  
  private processMovementInput(delta: number, inputController: InputController): void {
    // Reset direction
    this.direction.set(0, 0, 0);
    
    // Get movement input and create movement vector
    const forward = new THREE.Vector3(0, 0, -1);
    const right = new THREE.Vector3(-1, 0, 0);
    
    if (inputController.isKeyPressed('KeyW')) {
      this.direction.add(forward);
    }
    if (inputController.isKeyPressed('KeyS')) {
      this.direction.sub(forward);
    }
    if (inputController.isKeyPressed('KeyA')) {
      this.direction.add(right);
    }
    if (inputController.isKeyPressed('KeyD')) {
      this.direction.sub(right);
    }
    
    // Normalize direction if moving diagonally
    if (this.direction.lengthSq() > 0) {
      this.direction.normalize();
    }
    
    // Apply camera's horizontal rotation to movement direction
    this.direction.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.cameraHorizontalAngle);
    
    // Move character directly without collision check first
    const movement = this.direction.multiplyScalar(this.moveSpeed * delta);
    this.character.position.add(movement);
    
    // Update character rotation to face movement direction
    if (this.direction.lengthSq() > 0) {
      const targetAngle = Math.atan2(this.direction.x, this.direction.z);
      this.character.rotation.y = targetAngle;
    }
  }
  
  private applyGravity(delta: number, collisionSystem: CollisionSystem): void {
    if (!this.isGrounded) {
      // Apply gravity to velocity
      this.velocity.y -= this.gravity * delta;
      
      // Update position with velocity
      this.character.position.y += this.velocity.y * delta;
      
      // Find ground height
      const groundHeight = collisionSystem.findGroundHeight(this.character.position);
      
      // Check if character is on the ground
      if (this.character.position.y <= groundHeight) {
        this.character.position.y = groundHeight;
        this.velocity.y = 0;
        this.isGrounded = true;
        this.isJumping = false;
      }
    }
  }
  
  private handleJump(inputController: InputController, collisionSystem: CollisionSystem): void {
    if (inputController.isKeyPressed('Space') && this.isGrounded && !this.isJumping) {
      this.velocity.y = this.jumpForce;
      this.isJumping = true;
      this.isGrounded = false;
    }
  }

  private updateAnimations(): void {
    // Update animations based on movement
    if (this.isWalking) {
      this.walkAnimations.getAll().forEach(tween => tween.resume());
    } else {
      this.walkAnimations.getAll().forEach(tween => tween.pause());
      
      // Reset limb rotations when stopping
      if (this.leftArmMesh) this.leftArmMesh.rotation.x = 0;
      if (this.rightArmMesh) this.rightArmMesh.rotation.x = 0;
      if (this.leftLegMesh) this.leftLegMesh.rotation.x = 0;
      if (this.rightLegMesh) this.rightLegMesh.rotation.x = 0;
    }
  }

  private updateCameraPosition(camera: THREE.PerspectiveCamera, inputController: InputController, delta: number): void {
    // Update camera angles based on input
    if (inputController.getPointerLockState() || inputController.isMobile) {
      // Update camera horizontal rotation
      this.cameraHorizontalAngle -= inputController.getMouseDelta().x * this.mouseSensitivity;
      
      // Update camera vertical angle with clamping
      this.cameraVerticalAngle += inputController.getMouseDelta().y * this.mouseSensitivity;
      this.cameraVerticalAngle = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, this.cameraVerticalAngle));
    }
    
    // Handle mouse wheel for zoom (desktop only)
    if (!inputController.isMobile) {
      const wheelDelta = inputController.getWheelDelta();
      if (wheelDelta !== 0) {
        this.cameraDistance = Math.max(
          this.minCameraDistance,
          Math.min(this.maxCameraDistance, this.cameraDistance - wheelDelta * 0.001)
        );
      }
    }
    
    // Update camera position
    if (this.camera) {
      // Calculate camera target position
      const cameraOffset = new THREE.Vector3(
        Math.sin(this.cameraHorizontalAngle) * this.cameraDistance * Math.cos(this.cameraVerticalAngle),
        Math.sin(this.cameraVerticalAngle) * this.cameraDistance + 2,
        Math.cos(this.cameraHorizontalAngle) * this.cameraDistance * Math.cos(this.cameraVerticalAngle)
      );
      
      // Set camera position relative to character
      const targetPosition = this.character.position.clone().add(new THREE.Vector3(0, 1.5, 0));
      this.camera.position.copy(targetPosition.add(cameraOffset));
      
      // Make camera look at character
      const lookAtPosition = this.character.position.clone().add(new THREE.Vector3(0, 1.5, 0));
      this.camera.lookAt(lookAtPosition);
    }
  }

  setCamera(camera: THREE.PerspectiveCamera): void {
    this.camera = camera;
  }

  setInputController(inputController: InputController): void {
    this.inputController = inputController;
  }

  public getIsWalking(): boolean {
    return this.isWalking;
  }

  public startDancing(): void {
    // Implementation needed
  }

  public stopDancing(): void {
    // Implementation needed
  }

  public getIsDancing(): boolean {
    return false;
  }

  public getCameraHorizontalAngle(): number {
    return this.cameraHorizontalAngle;
  }

  public getPosition(): THREE.Vector3 {
    return this.character.position.clone();
  }
}

export async function createCharacter(
  scene: THREE.Scene,
  loadingManager: LoadingManager,
  playerName: string = 'Player'
): Promise<{ model: THREE.Group; controller: CharacterControllerBase }> {
  // Create a group to hold the character
  const characterGroup = new THREE.Group();
  characterGroup.name = 'character';
  
  // Load character model and animations
  const { model, mixer, animations } = await loadCharacterWithAnimations(loadingManager);
  
  // Add the model to the character group
  characterGroup.add(model);
  
  // Set initial position and rotation
  characterGroup.position.set(0, 0, 10);
  characterGroup.rotation.y = Math.PI;
  
  // Add name label above head
  const nameLabel = createTextSprite(playerName);
  nameLabel.position.set(0, 2.5, 0); // Position higher above player's head
  characterGroup.add(nameLabel);

  // Create character controller with animations
  const controller = new CharacterControllerFBX(characterGroup, mixer, animations);
  
  scene.add(characterGroup);
  
  return {
    model: characterGroup,
    controller
  };
}

// Function to load FBX model and animations
async function loadCharacterWithAnimations(loadingManager: LoadingManager): Promise<{ 
  model: THREE.Object3D; 
  mixer: THREE.AnimationMixer;
  animations: { 
    idle: THREE.AnimationAction; 
    walking: THREE.AnimationAction; 
  }
}> {
  return new Promise((resolve, reject) => {
    const fbxLoader = new FBXLoader(loadingManager.getManager());
    
    // Load walking animation which includes the model
    fbxLoader.load('/assets/walking.fbx', (walkingData: THREE.Group) => {
      // Scale the model appropriately
      walkingData.scale.set(0.013, 0.013, 0.013);
      
      // Create animation mixer
      const mixer = new THREE.AnimationMixer(walkingData);
      const walkingAnimation = mixer.clipAction(walkingData.animations[0]);
      walkingAnimation.setEffectiveWeight(0); // Start with weight 0
      walkingAnimation.play(); // Play but with 0 weight
      
      // Load idle animation
      fbxLoader.load('/assets/idle.fbx', (idleData: THREE.Group) => {
        const idleAnimation = mixer.clipAction(idleData.animations[0]);
        idleAnimation.setEffectiveWeight(1); // Start with full weight
        idleAnimation.play();
        
        // Return the model and animations
        resolve({
          model: walkingData,
          mixer: mixer,
          animations: {
            idle: idleAnimation,
            walking: walkingAnimation
          }
        });
      }, 
      // Progress callback for idle animation
      (xhr) => {
        // No longer showing progress bar
      }, 
      // Error callback for idle animation
      (error) => {
        console.error('Error loading idle animation:', error);
        reject(error);
      });
    }, 
    // Progress callback for walking animation/model
    (xhr) => {
      // No longer showing progress bar
    }, 
    // Error callback for walking animation/model
    (error) => {
      console.error('Error loading walking animation/model:', error);
      reject(error);
    });
  });
}

// Character controller class for FBX models with animations
export class CharacterControllerFBX implements CharacterControllerBase {
  private character: THREE.Group;
  private velocity: THREE.Vector3;
  private direction: THREE.Vector3;
  private rotation: THREE.Euler;
  private moveSpeed: number;
  private turnSpeed: number;
  private jumpForce: number;
  private gravity: number;
  private isJumping: boolean;
  private isGrounded: boolean;
  private height: number;
  private mouseSensitivity: number;
  private isWalking: boolean;
  private camera: THREE.PerspectiveCamera | null;
  private cameraVerticalAngle: number;
  private cameraHorizontalAngle: number;
  private cameraDistance: number;
  private minCameraDistance: number;
  private maxCameraDistance: number;
  private inputController: InputController | null;
  private nameLabel: THREE.Sprite | null = null;
  private mixer: THREE.AnimationMixer;
  private animations: { 
    idle: THREE.AnimationAction; 
    walking: THREE.AnimationAction;
    dancing?: THREE.AnimationAction;
  };
  private isDancing: boolean = false;
  private justStoppedDancing: boolean = false;
  private danceStartTime: number = 0;
  private danceCooldown: number = 1000; // 1 second cooldown
  
  constructor(character: THREE.Group, mixer: THREE.AnimationMixer, animations: { idle: THREE.AnimationAction; walking: THREE.AnimationAction }) {
    this.character = character;
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.direction = new THREE.Vector3(0, 0, 0);
    this.rotation = new THREE.Euler(0, 0, 0);
    this.moveSpeed = 5;
    this.turnSpeed = 2.5;
    this.jumpForce = 5;
    this.gravity = 15;
    this.isJumping = false;
    this.isGrounded = true;
    this.height = 1.8;
    this.mouseSensitivity = 0.002;
    this.isWalking = false;
    
    // Animation properties
    this.mixer = mixer;
    this.animations = animations;
    
    // Set initial animation
    this.animations.idle.play();
    
    // Camera settings
    this.camera = null;
    this.cameraVerticalAngle = 0;
    this.cameraHorizontalAngle = Math.PI;
    this.cameraDistance = 5;
    this.minCameraDistance = 2;
    this.maxCameraDistance = 10;
    this.inputController = null;
    
    // Setup mouse capture
    this.setupMouseCapture();

    // Find name label
    this.character.traverse((child) => {
      if (child instanceof THREE.Sprite) {
        this.nameLabel = child;
      }
    });
  }
  
  private setupMouseCapture(): void {
    // Add click event listener to capture mouse only on non-mobile devices
    document.addEventListener('click', () => {
      if (this.inputController && !this.inputController.isMobile && !this.inputController.getPointerLockState()) {
        this.inputController.requestPointerLock();
      }
    });
  }
  
  public update(delta: number, inputController: InputController, collisionSystem: CollisionSystem, camera: THREE.Camera): void {
    // If dancing, only update mixer and camera
    if (this.isDancing) {
      this.mixer.update(delta);
      
      // Update camera position
      if (this.camera) {
        const cameraOffset = new THREE.Vector3(
          Math.sin(this.cameraHorizontalAngle) * this.cameraDistance * Math.cos(this.cameraVerticalAngle),
          Math.sin(this.cameraVerticalAngle) * this.cameraDistance,
          Math.cos(this.cameraHorizontalAngle) * this.cameraDistance * Math.cos(this.cameraVerticalAngle)
        );
        
        const targetPosition = this.character.position.clone().add(new THREE.Vector3(0, 1.5, 0));
        this.camera.position.copy(targetPosition.add(cameraOffset));
        
        const lookAtPosition = this.character.position.clone().add(new THREE.Vector3(0, 1.5, 0));
        this.camera.lookAt(lookAtPosition);
      }
      return;
    }

    // If we just stopped dancing, force a mixer update and reset the flag
    if (this.justStoppedDancing) {
      console.log("Just stopped dancing - extra cleanup in update cycle");
      
      // Force multiple mixer updates to ensure animation transition
      this.mixer.update(delta);
      
      // Make sure all animations are in proper state
      if (this.animations.dancing) {
        try {
          console.log("Extra cleanup for dance animation in update");
          this.animations.dancing.stop();
          this.animations.dancing.enabled = false;
          this.animations.dancing.setEffectiveWeight(0);
          
          // Remove any crossfade influences and fully reset the animation
          this.animations.dancing.reset();
          
          // Force the idle animation to take over completely
          this.animations.idle.enabled = true;
          this.animations.idle.reset();
          this.animations.idle.setEffectiveTimeScale(1);
          this.animations.idle.setEffectiveWeight(1);
          this.animations.idle.play();
          
          // Force update to apply changes
          this.mixer.update(0.01);
        } catch (e) {
          console.error("Error in dance animation cleanup:", e);
        }
      }
      
      // We'll keep this flag for one more frame and then clear it in the next update
      if (this.justStoppedDancing) {
        setTimeout(() => {
          this.justStoppedDancing = false;
          console.log("Dance transition handled in update cycle");
        }, 100);
      }
    }

    // Store input controller reference
    if (!this.inputController) {
      this.setInputController(inputController);
    }

    this.updateMovement(delta, inputController, collisionSystem);
    this.updateRotation(delta, inputController);
    this.applyGravity(delta, collisionSystem);
    this.handleJump(inputController, collisionSystem);
    
    // Update animation mixer
    this.mixer.update(delta);
    
    // Check if character is moving
    const isMoving = this.direction.lengthSq() > 0;
    
    // Switch animations based on movement
    if (isMoving && !this.isWalking) {
      this.isWalking = true;
      
      // Make sure dancing is completely stopped
      if (this.animations.dancing) {
        this.animations.dancing.stop();
        this.animations.dancing.enabled = false;
        this.animations.dancing.setEffectiveWeight(0);
      }
      
      // Crossfade from idle to walking
      this.animations.walking.reset();
      this.animations.walking.enabled = true;
      this.animations.walking.setEffectiveTimeScale(1);
      this.animations.walking.setEffectiveWeight(1);
      this.animations.walking.crossFadeFrom(this.animations.idle, 0.2, true);
      this.animations.walking.play();
    } else if (!isMoving && this.isWalking) {
      this.isWalking = false;
      
      // Make sure dancing is completely stopped
      if (this.animations.dancing) {
        this.animations.dancing.stop();
        this.animations.dancing.enabled = false;
        this.animations.dancing.setEffectiveWeight(0);
      }
      
      // Crossfade from walking to idle
      this.animations.idle.reset();
      this.animations.idle.enabled = true;
      this.animations.idle.setEffectiveTimeScale(1);
      this.animations.idle.setEffectiveWeight(1);
      this.animations.idle.crossFadeFrom(this.animations.walking, 0.2, true);
      this.animations.idle.play();
    } else if (!isMoving && !this.isWalking) {
      // If we're standing still, ensure idle animation is playing
      if (!this.animations.idle.isRunning()) {
        this.animations.idle.reset();
        this.animations.idle.enabled = true;
        this.animations.idle.setEffectiveTimeScale(1);
        this.animations.idle.setEffectiveWeight(1);
        this.animations.idle.play();
      }
    }
    
    // Check for interactive objects near the player
    if (window.checkInteractiveObjects) {
      window.checkInteractiveObjects(this.character.position);
    }

    // Update name label to face camera
    if (this.nameLabel && camera) {
      this.nameLabel.quaternion.copy(camera.quaternion);
    }

    // Update camera position
    if (this.camera) {
      // Calculate camera target position
      const cameraOffset = new THREE.Vector3(
        Math.sin(this.cameraHorizontalAngle) * this.cameraDistance * Math.cos(this.cameraVerticalAngle),
        Math.sin(this.cameraVerticalAngle) * this.cameraDistance,
        Math.cos(this.cameraHorizontalAngle) * this.cameraDistance * Math.cos(this.cameraVerticalAngle)
      );
      
      // Set camera position relative to character
      const targetPosition = this.character.position.clone().add(new THREE.Vector3(0, 1.5, 0));
      this.camera.position.copy(targetPosition.add(cameraOffset));
      
      // Make camera look at character
      const lookAtPosition = this.character.position.clone().add(new THREE.Vector3(0, 1.5, 0));
      this.camera.lookAt(lookAtPosition);
    }
  }
  
  private updateMovement(delta: number, inputController: InputController, collisionSystem: CollisionSystem): void {
    // Reset direction
    this.direction.set(0, 0, 0);
    
    // Get movement input and create movement vector
    const forward = new THREE.Vector3(0, 0, -1);
    const right = new THREE.Vector3(-1, 0, 0);
    
    if (inputController.isKeyPressed('KeyW')) {
      this.direction.add(forward);
    }
    if (inputController.isKeyPressed('KeyS')) {
      this.direction.sub(forward);
    }
    if (inputController.isKeyPressed('KeyA')) {
      this.direction.add(right);
    }
    if (inputController.isKeyPressed('KeyD')) {
      this.direction.sub(right);
    }
    
    // Normalize direction if moving diagonally
    if (this.direction.lengthSq() > 0) {
      this.direction.normalize();
    }
    
    // Apply camera's horizontal rotation to movement direction
    this.direction.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.cameraHorizontalAngle);
    
    // Move character directly without collision check first
    const movement = this.direction.multiplyScalar(this.moveSpeed * delta);
    this.character.position.add(movement);
    
    // Update character rotation to face movement direction
    if (this.direction.lengthSq() > 0) {
      const targetAngle = Math.atan2(this.direction.x, this.direction.z);
      this.character.rotation.y = targetAngle;
    }
    
    // Then adjust height based on ground
    const groundHeight = collisionSystem.findGroundHeight(this.character.position);
    if (this.isGrounded) {
      this.character.position.y = groundHeight;
    }
  }
  
  private updateRotation(delta: number, inputController: InputController): void {
    // Get mouse/touch delta for both mobile and desktop
    const mouseDelta = inputController.getMouseDelta();
    
    // Update camera angles based on input
    if (inputController.getPointerLockState() || inputController.isMobile) {
      // Update camera horizontal rotation
      this.cameraHorizontalAngle -= mouseDelta.x * this.mouseSensitivity;
      
      // Update camera vertical angle with clamping
      this.cameraVerticalAngle += mouseDelta.y * this.mouseSensitivity;
      this.cameraVerticalAngle = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, this.cameraVerticalAngle));
    }
    
    // Handle mouse wheel for zoom (desktop only)
    if (!inputController.isMobile) {
      const wheelDelta = inputController.getWheelDelta();
      if (wheelDelta !== 0) {
        this.cameraDistance = Math.max(
          this.minCameraDistance,
          Math.min(this.maxCameraDistance, this.cameraDistance - wheelDelta * 0.001)
        );
      }
    }
  }
  
  private applyGravity(delta: number, collisionSystem: CollisionSystem): void {
    // Apply gravity if not grounded
    if (!this.isGrounded) {
      this.velocity.y -= this.gravity * delta;
      this.character.position.y += this.velocity.y * delta;
    }
    
    // Check if character is on the ground
    const groundHeight = collisionSystem.findGroundHeight(this.character.position);
    if (this.character.position.y <= groundHeight) {
      this.character.position.y = groundHeight;
      this.velocity.y = 0;
      this.isGrounded = true;
      this.isJumping = false;
    }
  }
  
  private handleJump(inputController: InputController, collisionSystem: CollisionSystem): void {
    if (inputController.isKeyPressed('Space') && this.isGrounded && !this.isJumping) {
      this.velocity.y = this.jumpForce;
      this.isGrounded = false;
      this.isJumping = true;
    }
  }
  
  setCamera(camera: THREE.PerspectiveCamera): void {
    this.camera = camera;
  }
  
  setInputController(inputController: InputController): void {
    this.inputController = inputController;
  }
  
  public getIsWalking(): boolean {
    return this.isWalking;
  }

  public startDancing(): void {
    // Check for cooldown after stopping dance
    const currentTime = Date.now();
    const lastDanceTime = this.justStoppedDancing ? currentTime - 200 : 0; // Use justStoppedDancing as indicator
    if (currentTime - lastDanceTime < 1000) { // 1 second cooldown
      console.log("Dance cooldown active, ignoring dance request");
      return;
    }

    if (this.isDancing || !this.mixer) {
      console.log("Already dancing or mixer not available");
      return;
    }

    console.log("startDancing: Starting dance animation and disabling movement");
    
    // Stop any ongoing movement
    this.direction.set(0, 0, 0);
    this.velocity.set(0, 0, 0);
    
    // Make sure walking is stopped
    this.isWalking = false;
    
    // First, make sure current animations are properly configured
    this.animations.idle.enabled = true;
    this.animations.walking.enabled = false;
    
    // Always reload the dance animation for consistency
    const fbxLoader = new FBXLoader();
    console.log("Loading dance animation...");
    fbxLoader.load('/assets/dancing.fbx', (danceData: THREE.Group) => {
      console.log("Dance animation loaded successfully");
      
      // If we had a previous dancing animation, clean it up
      if (this.animations.dancing) {
        this.animations.dancing.stop();
        this.animations.dancing.enabled = false;
        this.animations.dancing.setEffectiveWeight(0);
        
        // Get the clip and uncache it
        try {
          const oldClip = this.animations.dancing.getClip();
          this.mixer.uncacheClip(oldClip);
        } catch (e) {
          console.log("Error uncaching old dance clip:", e);
        }
      }
      
      // Create a fresh dance animation
      this.animations.dancing = this.mixer.clipAction(danceData.animations[0]);
      this.animations.dancing.setEffectiveTimeScale(1);
      this.animations.dancing.setEffectiveWeight(1);
      this.animations.dancing.setLoop(THREE.LoopRepeat, Infinity);
      this.animations.dancing.enabled = true;
      
      // Disable other animations
      this.animations.walking.enabled = false;
      this.animations.walking.setEffectiveWeight(0);
      
      // Get the current animation
      const currentAnim = this.isWalking ? this.animations.walking : this.animations.idle;
      
      // Stop current animation and crossfade to dancing
      this.animations.dancing.reset();
      this.animations.dancing.crossFadeFrom(currentAnim, 0.2, true);
      this.animations.dancing.play();
      
      // Update state
      this.isDancing = true;
      this.isWalking = false;
      
      // Force an update to kick-start the animation
      this.mixer.update(0.01);
      
      // Force a multiplayer update
      if (window.multiplayerManager && typeof window.multiplayerManager.forceDanceUpdate === 'function') {
        console.log("Forcing multiplayer dance update - now dancing");
        window.multiplayerManager.forceDanceUpdate(true);
      }
      
      console.log("Dance animation started - movement controls disabled");
    }, 
    // Progress callback
    (xhr) => {
      console.log(`Loading dance animation: ${(xhr.loaded / xhr.total * 100).toFixed(0)}%`);
    },
    // Error callback
    (error) => {
      console.error("Error loading dance animation:", error);
    });
  }

  public stopDancing(): void {
    if (!this.isDancing) {
      console.log("Not dancing, can't stop");
      return;
    }

    console.log("Stopping dance animation");
    
    // Mark as not dancing immediately to enable controls
    this.isDancing = false;
    
    // Reset walking state
    this.isWalking = false;
    
    if (this.animations && this.animations.dancing) {
      try {
        // Make sure the dance animation stops completely
        this.animations.dancing.stop();
        this.animations.dancing.reset();
        this.animations.dancing.enabled = false;
        this.animations.dancing.setEffectiveWeight(0);
        
        // Completely unset the dance animation from the mixer to force it to stop
        if (this.mixer) {
          // Get the clip from the action correctly
          const clip = this.animations.dancing.getClip();
          // Uncache the clip and action
          this.mixer.uncacheClip(clip);
        }
        
        // Explicitly enable and play idle animation with full weight
        this.animations.idle.reset();
        this.animations.idle.enabled = true;
        this.animations.idle.setEffectiveTimeScale(1);
        this.animations.idle.setEffectiveWeight(1);
        this.animations.idle.play();
        
        // Make sure walking is stopped and reset
        if (this.animations.walking) {
          this.animations.walking.stop();
          this.animations.walking.reset();
          this.animations.walking.setEffectiveWeight(0);
        }
        
        // Force multiple mixer updates to apply changes immediately
        if (this.mixer) {
          this.mixer.update(0);
          this.mixer.update(0.01);
        }
      } catch (e) {
        console.error("Error stopping dance animation:", e);
        // Fallback if there's an error with the animation system
        this.animations.dancing.stop();
        this.animations.idle.play();
      }
    }
    
    // Set flag to indicate we just stopped dancing (for cooldown)
    this.justStoppedDancing = true;
    
    // Force recreation of dancing animation next time (by deleting the current one)
    setTimeout(() => {
      if (this.animations) {
        this.animations.dancing = undefined;
        console.log("Dance animation reference removed for fresh load next time");
      }
    }, 100);
    
    // Clear justStoppedDancing flag after cooldown period
    setTimeout(() => {
      this.justStoppedDancing = false;
      console.log("Dance cooldown ended");
    }, 1500);

    // Force a multiplayer update - SIMPLIFIED APPROACH
    if (window.multiplayerManager && typeof window.multiplayerManager.forceDanceUpdate === 'function') {
      console.log("Forcing multiplayer dance stop update");
      window.multiplayerManager.forceDanceUpdate(false);
    }
  }

  public getIsDancing(): boolean {
    return this.isDancing;
  }

  public getCameraHorizontalAngle(): number {
    return this.cameraHorizontalAngle;
  }

  public getPosition(): THREE.Vector3 {
    return this.character.position.clone();
  }
} 